import React from 'react';
import { User } from '../../../../../services/eventService';

interface RegisterUserModalProps {
  show: boolean;
  onClose: () => void;
  userSearchInput: string;
  setUserSearchInput: (value: string) => void;
  filteredUnregisteredUsers: User[];
  handleRegisterUser: (userId: string) => void;
}

// Component to render a single user card
const UserCard: React.FC<{
  user: User;
  onRegister: (userId: string) => void;
}> = ({ user, onRegister }) => (
  <div className="col">
    <div className="card h-100">
      <div className="card-body d-flex align-items-center">
        {/* Profile Image */}
        <img 
          src={user.picture 
            ? `data:image/png;base64,${user.picture}` 
            : "/static/images/user-profile-icon.png"
          } 
          className="rounded-circle me-3" 
          style={{width: '40px', height: '40px'}}
          alt={user.picture ? "User Image" : "Default User Image"} 
        />
        
        {/* User Information */}
        <div className="flex-grow-1">
          <div className="fw-bold">{user.firstname} {user.lastname}</div>
          <small className="text-muted">{user.mail}</small>
          {user.firm && <small className="text-muted d-block">({user.firm.abbrev})</small>}
        </div>
        
        {/* Register Button */}
        <button 
          className="btn btn-success btn-sm flex-shrink-0 ms-2"
          style={{ minWidth: '90px' }}
          onClick={() => onRegister(user.User_ID)}
        >
          <i className="bi bi-person-plus-fill me-1"></i>
          Register
        </button>
      </div>
    </div>
  </div>
);

// Search input with icon component
const SearchBox: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => (
  <div className="mb-4 position-relative">
    <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
    <input 
      type="text" 
      className="form-control form-control-lg ps-5" 
      placeholder="Search for users..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const RegisterUserModal: React.FC<RegisterUserModalProps> = ({
  show,
  onClose,
  userSearchInput,
  setUserSearchInput,
  filteredUnregisteredUsers,
  handleRegisterUser
}) => {
  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex={-1}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header bg-light">
            <h5 className="modal-title">
              <i className="bi bi-person-plus-fill me-2"></i>Register User for Event
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            {/* Search with icon */}
            <SearchBox 
              value={userSearchInput}
              onChange={setUserSearchInput}
            />

            {/* User cards container */}
            <div className="user-cards-container">
              {/* Counter badge at the top */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="badge bg-info">
                  {filteredUnregisteredUsers.length} users available
                </span>
              </div>

              {/* User cards grid */}
              {filteredUnregisteredUsers.length > 0 ? (
                <div className="row row-cols-1 row-cols-md-2 g-3">
                  {filteredUnregisteredUsers.map(user => (
                    <UserCard 
                      key={user.User_ID} 
                      user={user} 
                      onRegister={handleRegisterUser} 
                    />
                  ))}
                </div>
              ) : (
                // Empty state when no results found
                <div className="text-center py-5">
                  <i className="bi bi-search fs-1 text-muted"></i>
                  <p className="text-muted mt-2">No users found matching your search.</p>
                </div>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterUserModal;
